import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { getStorageItem } from 'app/adapters/storage';
import {
  buildDriverPortalRedirectUrl,
  shouldRedirectToDriverPortal,
} from 'app/pages/Login/utils/loginPermissions';
import { MyUserService } from 'app/services/api/MyUserService';
import { parseApiResponseError, redirectBase } from 'app/utils/error-handler';

/**
 * Auth gate that redirects driver-only SSO users to the driver portal
 * before the admin Dashboard (header/sidebar) ever mounts.
 */
export function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthUser = getStorageItem('isAuthUser');
  const [allowDashboard, setAllowDashboard] = useState(false);

  useEffect(() => {
    if (!isAuthUser || isAuthUser === 'false') {
      return;
    }

    let cancelled = false;

    async function gateDriverRedirect() {
      try {
        const { body: myUser } = await MyUserService.getMyUser();
        if (cancelled) {
          return;
        }
        if (shouldRedirectToDriverPortal(myUser.permissions ?? [])) {
          window.location.replace(buildDriverPortalRedirectUrl());
          return;
        }
        setAllowDashboard(true);
      } catch (error) {
        if (cancelled) {
          return;
        }
        const { status } = parseApiResponseError(error);
        if (status === 401 || status === 500) {
          redirectBase();
          return;
        }
        // Non-auth failures: let Dashboard surface the error.
        setAllowDashboard(true);
      }
    }

    gateDriverRedirect();

    return () => {
      cancelled = true;
    };
  }, [isAuthUser]);

  if (!isAuthUser || isAuthUser === 'false') {
    return <Navigate to="/" />;
  }

  if (!allowDashboard) {
    // Neutral gate — avoid admin Splash branding during role check / redirect.
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: 0,
          fontFamily: '"DM Sans", sans-serif',
          fontSize: 15,
          color: '#4b5563',
          background: '#f9fafb',
        }}
      >
        Loading…
      </div>
    );
  }

  return <>{children}</>;
}
